import { CommentActionType } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import type {
	CommentData,
	CommentsDataMap,
	DataMap,
	ReplyData,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { fg } from '@confluence/feature-gating';
import type { CommentLocation } from '@confluence/comments-panel-queries/entry-points/CommentLocation.ts';
import type {
	CommentInlineLocation,
	ActiveCommentInlineResolveProperties,
} from '@confluence/comments-panel-queries/entry-points/ActiveCommentsQuery.ts';

import { getCommentsSortedOldestToNewest } from './commentsPanelHelper.ts';
import { ViewValues } from './useCommentsPanel.ts';

type CommentWithRemovalStatus = Pick<CommentData | ReplyData, 'wasRemovedByAnotherUser'>;
type IsCommentValid = (comment: CommentData | ReplyData) => boolean;
type VisibleInlineCommentsByDanglingStatus = [CommentData[], CommentData[]];

type GetVisibleCommentThreadsOptions = {
	currentView?: ViewValues;
	commentsDataMap: CommentsDataMap;
	includeInlineComments: boolean;
	includeGeneralComments: boolean;
	orderedAnnotationIds: string[];
	isNewDanglingCommentExperienceEnabled?: boolean;
	isCommentValid?: IsCommentValid;
};

const isParentCommentOpen = (commentData: CommentData): boolean => commentData.isOpen;

const wasCommentRemovedByAnotherUser = (comment: CommentWithRemovalStatus): boolean =>
	Boolean(comment.wasRemovedByAnotherUser);

const wasCommentDeletedByAnotherUser = (comment: CommentWithRemovalStatus): boolean =>
	comment.wasRemovedByAnotherUser === CommentActionType.DELETE_COMMENT;

const isInlineLocation = (location: CommentLocation): location is CommentInlineLocation =>
	'inlineMarkerRef' in location;

const isParentComment = (comment: CommentData | ReplyData): comment is CommentData =>
	'isOpen' in comment;

type GetVisibleGeneralCommentsOptions = {
	isCommentValid: IsCommentValid;
	comments: DataMap;
};

const getVisibleGeneralComments = ({
	isCommentValid,
	comments,
}: GetVisibleGeneralCommentsOptions): CommentData[] =>
	Object.values(comments).reduce<CommentData[]>((acc, comment) => {
		if (isCommentValid(comment)) {
			acc.push({
				...comment,
				replies: comment.replies.filter(isCommentValid),
			});
		}
		return acc;
	}, []);

const getNextInlineResolveProperties = (
	inlineResolveProperties: ActiveCommentInlineResolveProperties | null,
	isDangling: boolean,
): { inlineResolveProperties?: ActiveCommentInlineResolveProperties } => {
	if (!inlineResolveProperties) return {};

	return {
		inlineResolveProperties: {
			...inlineResolveProperties,
			isDangling,
		},
	};
};

const updateCommentLocation = (
	comment: CommentData,
	isDangling: boolean,
): CommentData['location'] => {
	const commentLocation = comment.location;
	if (!isInlineLocation(commentLocation)) return commentLocation;

	return {
		...commentLocation,
		...getNextInlineResolveProperties(commentLocation.inlineResolveProperties, isDangling),
	} as CommentData['location'];
};

const getNextComment = (
	comment: CommentData,
	isDangling: boolean,
	isNewDanglingCommentExperienceEnabled: boolean,
): CommentData => {
	const { isUnread, replies, ...rest } = comment;
	const shouldMarkDanglingCommentsAndTheirRepliesAsRead =
		isDangling && isNewDanglingCommentExperienceEnabled;

	const updatedReplies = shouldMarkDanglingCommentsAndTheirRepliesAsRead
		? replies.map((reply) => ({ ...reply, isUnread: false }))
		: replies;

	return {
		...rest,
		isUnread: shouldMarkDanglingCommentsAndTheirRepliesAsRead ? false : isUnread,
		replies: updatedReplies,
		location: updateCommentLocation(comment, isDangling),
	};
};

const getVisibleInlineComments = (
	isCommentValid: IsCommentValid,
	comments: DataMap,
	orderedAnnotationIds: string[],
	isNewDanglingCommentExperienceEnabled: boolean,
): VisibleInlineCommentsByDanglingStatus => {
	const activeMarkerRefs = new Set(orderedAnnotationIds);
	const nonDangling: CommentData[] = [];
	const dangling: CommentData[] = [];

	const validComments = Object.values(comments).reduce<CommentData[]>((acc, comment) => {
		if (isCommentValid(comment)) {
			acc.push({
				...comment,
				replies: comment.replies.filter(isCommentValid),
			});
		}
		return acc;
	}, []);

	for (const comment of validComments) {
		const commentLocation = comment.location;
		const inlineMarkerRef = isInlineLocation(commentLocation)
			? commentLocation.inlineMarkerRef
			: null;

		const isDangling = !inlineMarkerRef || !activeMarkerRefs.has(inlineMarkerRef);
		const updatedComment = getNextComment(
			comment,
			isDangling,
			isNewDanglingCommentExperienceEnabled,
		);

		if (isDangling) {
			dangling.push(updatedComment);
		} else {
			nonDangling.push(updatedComment);
		}
	}

	return [nonDangling, dangling];
};

export const isCommentResolved = (comment: CommentData | ReplyData): boolean => {
	if (wasCommentDeletedByAnotherUser(comment)) return false;

	return isParentComment(comment) ? !isParentCommentOpen(comment) : true;
};

export const isCommentOpen = (comment: CommentData | ReplyData): boolean => {
	if (wasCommentRemovedByAnotherUser(comment)) return false;

	return isParentComment(comment) ? isParentCommentOpen(comment) : true;
};

const sortInlineCommentsByOrderedActiveAnnotationIdList = (
	comments: CommentData[],
	orderedAnnotationIds: string[],
): CommentData[] => {
	const commentsByMarkerRef = new Map(
		comments.map((comment) => {
			const commentLocation = comment.location;
			const inlineMarkerRef = isInlineLocation(commentLocation)
				? commentLocation.inlineMarkerRef
				: null;

			return [inlineMarkerRef, comment];
		}),
	);

	return orderedAnnotationIds
		.map((id) => commentsByMarkerRef.get(id))
		.filter((comment): comment is CommentData => comment !== undefined);
};

const getVisibleInlineThreadsForView = (
	currentView: ViewValues,
	sortedNonDanglingComments: CommentData[],
	danglingComments: CommentData[],
) => {
	switch (currentView) {
		case ViewValues.OPEN:
		case ViewValues.UNREAD:
			return sortedNonDanglingComments;
		case ViewValues.DELETED_CONTENT:
			return danglingComments;
		default:
			return [...sortedNonDanglingComments, ...danglingComments];
	}
};

type GetVisibleInlineThreadsOptions = {
	isCommentValid: IsCommentValid;
	commentsDataMap: CommentsDataMap;
	orderedAnnotationIds: string[];
	currentView: ViewValues;
	isNewDanglingCommentExperienceEnabled: boolean;
};

const getVisibleInlineThreads = ({
	isCommentValid,
	commentsDataMap,
	orderedAnnotationIds,
	currentView,
	isNewDanglingCommentExperienceEnabled,
}: GetVisibleInlineThreadsOptions): CommentData[] => {
	const [nonDanglingComments, danglingComments] = getVisibleInlineComments(
		isCommentValid,
		commentsDataMap.inline,
		orderedAnnotationIds,
		isNewDanglingCommentExperienceEnabled,
	);

	const sortedNonDanglingComments = sortInlineCommentsByOrderedActiveAnnotationIdList(
		nonDanglingComments,
		orderedAnnotationIds,
	);

	const visibleInlineThreadsForView = isNewDanglingCommentExperienceEnabled
		? getVisibleInlineThreadsForView(currentView, sortedNonDanglingComments, danglingComments)
		: [...sortedNonDanglingComments, ...danglingComments];

	return fg('confluence_frontend_content_wrapper')
		? visibleInlineThreadsForView
		: sortedNonDanglingComments;
};

type GetIsCommentValidOptions = {
	currentView: ViewValues;
};

const getIsCommentValid = ({ currentView }: GetIsCommentValidOptions): IsCommentValid => {
	switch (currentView) {
		case ViewValues.RESOLVED:
			return isCommentResolved;
		default:
			return isCommentOpen;
	}
};

export const getVisibleCommentThreads = ({
	commentsDataMap,
	orderedAnnotationIds,
	includeInlineComments,
	includeGeneralComments,
	currentView = ViewValues.OPEN,
	isNewDanglingCommentExperienceEnabled = fg('confluence_frontend_new_dangling_comments_ux'),
	isCommentValid: providedIsCommentValid,
}: GetVisibleCommentThreadsOptions): CommentData[] => {
	const threads: CommentData[] = [];

	const isCommentValid =
		providedIsCommentValid ??
		getIsCommentValid({
			currentView,
		});

	if (includeInlineComments) {
		const inlineThreads = getVisibleInlineThreads({
			isCommentValid,
			commentsDataMap,
			orderedAnnotationIds,
			currentView,
			isNewDanglingCommentExperienceEnabled,
		});
		threads.push(...inlineThreads);
	}

	if (includeGeneralComments) {
		const generalThreads = getVisibleGeneralComments({
			isCommentValid,
			comments: commentsDataMap.general,
		}).sort(getCommentsSortedOldestToNewest);
		threads.push(...generalThreads);
	}

	return threads;
};
